/* eslint-disable max-len */
import React, {useEffect, useState, useMemo} from 'react';
import Button from '../Button';
import Checkbox from '../forms/Checkbox';
import {default as Dialog} from '../Dialog2';
import {BrandType} from '@sinclair-digital/stl-common/src/models/Brand';
import styles from './MediaAccessRights.scss';

interface propTypes {
    isModalOpen: boolean;
    selectedBrandUuids: string[];         // List of brand uuids that have access permission for this media. Unlike parent component empty array means no brand selected
    availableBrandUuids: string[];        // List of brand uuids that the user has permission for
    availableAdminBrandUuids: string[];   // List of brand uuids that the user has admin permission for
    allBrands: BrandType[];               // List of all brands in the system
    toggleModal: () => void;
    onSave: (brandUuidList: string[]) => void;
}

/**
 * Notes:
 * - Users can only add a brand if they have an admin permission for that brand (property admin, global admin, regional producers)
 * - Users can remove a brand if they have access to that brand (both admin & non-admin)
 * - Any brand that the user does not have access to will have disabled checkbox and is not editable
 * - Any brand checkbox where user does not have access to and is not already selected will be hidden
 */
export const MediaAccessRightsModal = ({
  isModalOpen,
  selectedBrandUuids = [],
  availableBrandUuids = [],
  availableAdminBrandUuids = [],
  allBrands,
  toggleModal,
  onSave,
}: propTypes) => {
  const [isSelectAllChecked, setIsSelectAllChecked] = useState<boolean>(false);
  const [_selectedBrandUuids, setSelectedBrandUuids] = useState<string[]>(selectedBrandUuids);

  // check if all brands are selected
  useEffect(() => {
    const areAllBrandsSelected = availableBrandUuids.every(uuid => {
      if (!allBrands.some(brand => brand.uuid === uuid)) return true; //ignore junk/test properties the user has access to
      return _selectedBrandUuids.includes(uuid);
    });
    setIsSelectAllChecked(areAllBrandsSelected);
  }, [_selectedBrandUuids]);

  // toggles on/off all brand checkbox. If a user does not have brand permission those brands will not be toggled
  const onSelectAllToggle = (isChecked: boolean) => {
    const noPermissionBrandList = selectedBrandUuids.filter(uuid => !availableBrandUuids.includes(uuid));
    if (isChecked) {
      setSelectedBrandUuids([...noPermissionBrandList, ...availableBrandUuids]);
    } else {
      setSelectedBrandUuids(noPermissionBrandList);
    }
    setIsSelectAllChecked(isChecked);
  };

  const onBrandCheckboxClick = (brandUuid: string) => {
    if (_selectedBrandUuids.includes(brandUuid)) {
      setSelectedBrandUuids(_selectedBrandUuids.filter(uuid => uuid !== brandUuid));
    } else {
      setSelectedBrandUuids([..._selectedBrandUuids, brandUuid]);
    }
  };

  const onSaveBrandList = () => {
    // pass empty array instead if all brands are selected
    if (allBrands.every(({uuid}) => _selectedBrandUuids.includes(uuid))) {
      onSave([]);
    } else {
      onSave(_selectedBrandUuids);
    }
    toggleModal();
  };

  /**
     * filtered and sorted brand list.
     * first filtered by:
     * brands which already has access permission OR brands that the user has admin permission for
     * then sorted by:
     * alphabetically and by availability of brand for the user
     */
  const brandList = useMemo(() => {
    return allBrands
      .filter(brand => selectedBrandUuids.includes(brand.uuid) || availableAdminBrandUuids.includes(brand.uuid))
      .sort((a, b) => a.callLetters.localeCompare(b.callLetters, 'en', {sensitivity: 'base', numeric: true}))
      .sort((a, b) => {
        const aIsAvailable = availableBrandUuids.includes(a.uuid);
        const bIsAvailable = availableBrandUuids.includes(b.uuid);

        if (aIsAvailable && !bIsAvailable) {
          return -1;
        }
        if (!aIsAvailable && bIsAvailable) {
          return 1;
        }
        return 0;
      });

  }, [allBrands, selectedBrandUuids, availableAdminBrandUuids]);

  return (
    <Dialog
      isOpen={isModalOpen}
      onClose={toggleModal}
      heading={
        <>
          <h1>Manage Permissions</h1>
          <p>Select stations able to access this media.</p>
          {brandList.length > 1 &&
          <Checkbox label="Select All" onChange={onSelectAllToggle} checked={isSelectAllChecked}/>
          }
        </>
      }
      headerClass={styles.modalHeader}
      contentClass={styles.modalContent}
      footer={
        <>
          <Button displayType="secondary" onClick={toggleModal}>cancel</Button>
          <Button displayType="primary" onClick={onSaveBrandList} disabled={_selectedBrandUuids.length === 0}>save</Button>
        </>
      }
      footerClass={styles.modalFooter}
      width={800}
      height={700}
    >
      <div className={styles.scrollSection}>
        {brandList.length === 0 && <div>This account has insufficient permissions </div>}
        {brandList.map(brand =>
          <Checkbox
            key={brand.uuid}
            className={styles.checkbox}
            label={brand.callLetters}
            checked={_selectedBrandUuids.includes(brand.uuid)}
            onChange={() => onBrandCheckboxClick(brand.uuid)}
            disabled={!availableBrandUuids.includes(brand.uuid)}
          />
        )}
      </div>
    </Dialog>
  );
};
